import { useState, useEffect, useCallback, useRef } from 'react';
import { getEmailDrafts, updateEmailDraft, type EmailDraft } from '../api';
import { useToast } from '../contexts/ToastContext';
import { formatTime, getInitials, parseFrom } from '../utils/format';

interface Props {
  chatbotId: string;
}

const PAGE_SIZE = 25;

function StatusDot({ status }: { status: string }) {
  const color = (status === 'saved_as_draft' || status === 'created') ? 'var(--color-success)' : status === 'error' ? 'var(--color-danger)' : 'var(--color-text-muted)';
  return (
    <div style={{
      width: '8px', height: '8px', borderRadius: '50%',
      background: color, flexShrink: 0,
    }} />
  );
}

function AutoResizeTextarea({ value, onChange, style }: { value: string; onChange: (v: string) => void; style?: React.CSSProperties }) {
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    if (ref.current) {
      ref.current.style.height = 'auto';
      ref.current.style.height = ref.current.scrollHeight + 'px';
    }
  }, [value]);
  return (
    <textarea
      ref={ref}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{
        width: '100%', border: '1px solid var(--color-border)', borderRadius: '8px',
        padding: '12px', fontSize: '14px', lineHeight: 1.7, fontFamily: 'inherit',
        resize: 'none', overflow: 'hidden', background: 'var(--color-bg-white)',
        color: 'var(--color-email-text)', ...style,
      }}
    />
  );
}

export default function EmailDraftsLog({ chatbotId }: Props) {
  const [drafts, setDrafts] = useState<EmailDraft[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editingBody, setEditingBody] = useState('');
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();

  const loadDrafts = useCallback(async (pageNum: number) => {
    setLoading(true);
    try {
      const res = await getEmailDrafts(chatbotId, PAGE_SIZE, pageNum * PAGE_SIZE);
      if (res.success && res.data) {
        setDrafts(res.data);
        setTotal(res.total || 0);
      }
    } catch { /* ignore */ }
    setLoading(false);
  }, [chatbotId]);

  useEffect(() => { loadDrafts(page); }, [page, loadDrafts]);
  useEffect(() => { setCopied(false); setIsEditing(false); }, [selectedId]);

  const selected = selectedId ? drafts.find(d => d.id === selectedId) : null;
  const hasDraftBody = !!(selected?.draft_body);

  const handleCopy = async () => {
    if (!selected?.draft_body) return;
    try {
      await navigator.clipboard.writeText(selected.draft_body);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy to clipboard');
    }
  };

  const handleStartEdit = () => {
    if (!selected?.draft_body) return;
    setEditingBody(selected.draft_body);
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditingBody('');
  };

  const handleSave = async () => {
    const draftId = selectedId;
    if (!draftId || !editingBody.trim()) return;
    setSaving(true);
    try {
      const res = await updateEmailDraft(chatbotId, draftId, editingBody);
      if (res.success) {
        setDrafts(prev => prev.map(d => d.id === draftId ? { ...d, draft_body: editingBody } : d));
        setIsEditing(false);
        toast.success('Draft updated');
      } else {
        toast.error(res.error || 'Failed to save');
      }
    } catch {
      toast.error('Failed to save draft');
    }
    setSaving(false);
  };

  // ========== DETAIL VIEW ==========
  if (selected) {
    const { name: senderName, email: senderEmail } = parseFrom(selected.original_from);
    const draftTo = selected.draft_to || selected.original_from;
    const avatarColors = ['#1a73e8', '#ea4335', '#34a853', '#fbbc04', '#8e24aa', '#00897b'];
    const avatarColor = avatarColors[senderName.charCodeAt(0) % avatarColors.length];

    return (
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {/* Toolbar */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '2px', padding: '4px 8px', borderBottom: '1px solid var(--color-email-border)', background: 'var(--color-email-surface)' }}>
          <button
            onClick={() => { setSelectedId(null); setIsEditing(false); }}
            style={{
              background: 'none', border: 'none', color: 'var(--color-email-text-secondary)', fontSize: '13px', fontWeight: 600,
              cursor: 'pointer', padding: '6px 12px', borderRadius: '4px', whiteSpace: 'nowrap',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-email-tag-bg)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6" /></svg>
              Back
            </span>
          </button>
          <div style={{ flex: 1 }} />
          {hasDraftBody && (
            <>
              <button
                onClick={handleCopy}
                title="Copy draft to clipboard"
                style={{
                  background: 'none', border: 'none', color: copied ? 'var(--color-success)' : 'var(--color-email-text-secondary)',
                  cursor: 'pointer', padding: '6px 10px', borderRadius: '4px', fontSize: '13px', fontWeight: 500,
                  display: 'flex', alignItems: 'center', gap: '4px',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-email-tag-bg)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
              >
                {copied ? (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12" /></svg>
                ) : (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>
                )}
                {copied ? 'Copied' : 'Copy'}
              </button>
              {!isEditing && (
                <button
                  onClick={handleStartEdit}
                  title="Edit draft"
                  style={{
                    background: 'none', border: 'none', color: 'var(--color-email-text-secondary)',
                    cursor: 'pointer', padding: '6px 10px', borderRadius: '4px', fontSize: '13px', fontWeight: 500,
                    display: 'flex', alignItems: 'center', gap: '4px',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-email-tag-bg)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                  Edit
                </button>
              )}
            </>
          )}
          <span style={{
            padding: '3px 10px', borderRadius: '14px', fontSize: '12px', fontWeight: 600, marginLeft: '4px',
            background: (selected.status === 'saved_as_draft' || selected.status === 'created') ? 'var(--color-success-light)' : selected.status === 'error' ? 'var(--color-danger-light)' : 'var(--color-bg-secondary)',
            color: (selected.status === 'saved_as_draft' || selected.status === 'created') ? 'var(--color-success)' : selected.status === 'error' ? 'var(--color-danger)' : 'var(--color-text-secondary)',
          }}>
            {selected.status === 'saved_as_draft' ? 'Draft Saved' : selected.status === 'created' ? 'AI Draft Ready' : selected.status === 'error' ? 'Error' : selected.status}
          </span>
        </div>

        {/* Email content */}
        <div style={{ padding: '24px 28px 0' }}>
          {/* Subject */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
            <h2 style={{ fontSize: '22px', fontWeight: 400, color: 'var(--color-email-text)', margin: 0, lineHeight: 1.3 }}>
              {selected.original_subject || '(no subject)'}
            </h2>
          </div>

          {/* Sender info */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '20px' }}>
            <div style={{
              width: '40px', height: '40px', borderRadius: '50%', background: avatarColor, color: 'white',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', fontWeight: 500,
              flexShrink: 0, marginTop: '2px',
            }}>
              {getInitials(senderName)}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                <span style={{ fontWeight: 600, fontSize: '14px', color: 'var(--color-email-text)' }}>{senderName}</span>
                <span style={{ fontSize: '13px', color: 'var(--color-email-text-secondary)' }}>&lt;{senderEmail}&gt;</span>
                <span style={{ marginLeft: 'auto', fontSize: '12px', color: 'var(--color-email-text-secondary)', flexShrink: 0 }}>
                  {formatTime(new Date(selected.created_at))}
                </span>
              </div>
              <div style={{ fontSize: '13px', color: 'var(--color-email-text-secondary)', marginTop: '1px' }}>to me</div>
            </div>
          </div>

          {/* Original email body */}
          <div className="email-detail-body" style={{ fontSize: '14px', lineHeight: 1.7, color: 'var(--color-email-text)', whiteSpace: 'pre-wrap', paddingBottom: '24px' }}>
            {selected.original_body || '(email body not available)'}
          </div>
        </div>

        {/* AI Draft Reply Box */}
        <div className="email-draft-reply" style={{
          marginRight: '28px',
          marginBottom: '24px',
          borderRadius: '12px',
          border: '1px solid var(--color-email-draft-border)',
          background: 'var(--color-email-row-bg)',
          overflow: 'hidden',
          animation: 'draftGlow 1.5s ease-out',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        }}>
          {selected.status === 'error' ? (
            <div style={{ padding: '16px 20px', color: 'var(--color-danger)', fontSize: '14px' }}>
              {selected.error_message || 'An error occurred while generating the response'}
            </div>
          ) : (
            <>
              {/* Draft header */}
              <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--color-email-tag-bg)', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{
                  display: 'inline-flex', alignItems: 'center', gap: '5px',
                  background: 'var(--color-email-accent)', color: 'white',
                  padding: '3px 10px', borderRadius: '14px', fontSize: '12px', fontWeight: 600,
                }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" stroke="none">
                    <path d="M12 2L9 9H2l6 4.5L5.5 22 12 17l6.5 5-2.5-8.5L22 9h-7z" />
                  </svg>
                  AI Draft
                </div>
                <span style={{ fontSize: '12px', color: 'var(--color-email-text-secondary)' }}>
                  {isEditing ? 'Editing draft' : 'Auto-generated draft response'}
                </span>
              </div>

              {/* Reply header */}
              <div style={{ padding: '10px 16px 0', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--color-email-text-secondary)" strokeWidth="2" style={{ transform: 'scaleX(-1)' }}>
                  <polyline points="9 17 4 12 9 7" /><path d="M20 18v-2a4 4 0 0 0-4-4H4" />
                </svg>
                <span style={{ fontSize: '13px', color: 'var(--color-email-text-secondary)' }}>
                  To: {draftTo}{selected.draft_cc ? `; Cc: ${selected.draft_cc}` : ''}
                </span>
              </div>

              {/* Draft body */}
              <div style={{ padding: '12px 16px 16px' }}>
                {isEditing ? (
                  <>
                    <AutoResizeTextarea value={editingBody} onChange={setEditingBody} />
                    <div style={{ display: 'flex', gap: '8px', marginTop: '12px', justifyContent: 'flex-end' }}>
                      <button onClick={handleCancelEdit} className="btn btn-secondary btn-small" disabled={saving}>Cancel</button>
                      <button onClick={handleSave} className="btn btn-primary btn-small" disabled={saving || !editingBody.trim()}>
                        {saving ? <><span className="spinner spinner-sm" /> Saving...</> : 'Save'}
                      </button>
                    </div>
                  </>
                ) : (
                  <div style={{ fontSize: '14px', lineHeight: 1.7, color: 'var(--color-email-text)', whiteSpace: 'pre-wrap' }}>
                    {selected.draft_body || '(draft not available)'}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  // ========== LIST VIEW ==========
  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid var(--color-email-border)' }}>
        <div>
          <h3 style={{ fontSize: '18px', fontWeight: 600, margin: 0 }}>
            Processed Emails
            {total > 0 && (
              <span style={{
                marginLeft: '10px', background: 'var(--color-email-tag-bg)', borderRadius: '12px',
                padding: '2px 10px', fontSize: '13px', fontWeight: 500, color: 'var(--color-email-text-secondary)',
              }}>
                {total}
              </span>
            )}
          </h3>
          <p style={{ fontSize: '14px', color: 'var(--color-email-text-secondary)', margin: '4px 0 0' }}>
            Emails processed by the AI with their generated draft responses
          </p>
        </div>
        <button
          onClick={() => loadDrafts(page)}
          disabled={loading}
          className="btn btn-secondary btn-small"
          style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
        >
          {loading ? <span className="spinner spinner-sm" /> : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
            </svg>
          )}
          Refresh
        </button>
      </div>

      {/* List */}
      {loading && drafts.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <div className="spinner" style={{ margin: '0 auto 16px' }} />
          <p style={{ color: 'var(--color-email-text-secondary)' }}>Loading...</p>
        </div>
      ) : drafts.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--color-text-muted)" strokeWidth="1.5" style={{ marginBottom: '16px' }}>
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
            <polyline points="22,6 12,13 2,6" />
          </svg>
          <p style={{ color: 'var(--color-text-secondary)', fontSize: '15px', fontWeight: 500 }}>No processed emails yet</p>
          <p style={{ color: 'var(--color-text-muted)', fontSize: '14px', marginTop: '4px' }}>When emails arrive and the AI generates draft responses, they will appear here.</p>
        </div>
      ) : (
        <>
          {drafts.map((draft) => {
            const { name: senderName } = parseFrom(draft.original_from);
            const snippet = draft.original_body ? draft.original_body.substring(0, 80).replace(/\n/g, ' ') : '';
            return (
              <div
                key={draft.id}
                onClick={() => setSelectedId(draft.id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0',
                  padding: '0 16px 0 0', height: '40px',
                  cursor: 'pointer',
                  background: 'var(--color-email-row-bg)',
                  borderLeft: '3px solid transparent',
                  borderBottom: '1px solid var(--color-email-border-light)',
                  fontSize: '14px',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-email-hover)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--color-email-row-bg)'; }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '0 12px 0 14px', flexShrink: 0 }}>
                  <StatusDot status={draft.status} />
                </div>
                <div className="email-row-sender" style={{ fontWeight: 400, color: 'var(--color-email-text)' }}>
                  {senderName}
                </div>
                <div style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  <span style={{ color: 'var(--color-email-text)' }}>{draft.original_subject || '(no subject)'}</span>
                  {snippet && <span style={{ color: 'var(--color-email-text-secondary)' }}> &mdash; {snippet}</span>}
                </div>
                <div className="email-row-time" style={{ color: 'var(--color-email-text-secondary)' }}>
                  {formatTime(new Date(draft.created_at))}
                </div>
              </div>
            );
          })}

          {/* Pagination */}
          {total > PAGE_SIZE && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 20px', borderTop: '1px solid var(--color-email-border)', background: 'var(--color-email-surface)' }}>
              <span style={{ fontSize: '13px', color: 'var(--color-email-text-secondary)' }}>
                Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total}
              </span>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => setPage(p => p - 1)}
                  disabled={page === 0}
                  className="btn btn-secondary btn-small"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage(p => p + 1)}
                  disabled={(page + 1) * PAGE_SIZE >= total}
                  className="btn btn-secondary btn-small"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
